import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Calendar, Clock, Video, ChevronLeft, ChevronRight, Globe, Check } from 'lucide-react';
import { useScreenInit } from '../useScreenInit.js';
export function BookDemo() {
  useScreenInit();
  const [selectedDate, setSelectedDate] = useState<number | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [isBooked, setIsBooked] = useState(false);
  const timeSlots = ['9:00 AM', '9:30 AM', '10:00 AM', '10:30 AM', '1:00 PM', '1:30 PM', '2:00 PM', '3:00 PM'];
  const handleBook = () => {
    setIsBooked(true);
  };
  return <div className="min-h-screen bg-[#1C1510] flex items-center justify-center py-24 px-6 relative overflow-hidden" data-id="element-3382">
      <div className="absolute inset-0 dot-matrix-dark opacity-50 pointer-events-none" data-id="element-3383" />
      <div aria-hidden="true" className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[600px] rounded-full bg-[#F97316]/[0.08] blur-[120px]" data-id="element-3384" />

      <div className="relative z-10 w-full max-w-5xl" data-id="element-3385">
        {!isBooked ? <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95
      }} className="bg-[#2B211A] border border-white/10 rounded-3xl shadow-2xl overflow-hidden flex flex-col md:flex-row" data-id="element-3386">
            {/* Left info panel */}
            <div className="p-8 md:p-10 border-b md:border-b-0 md:border-r border-white/5 md:w-1/3 bg-black/20" data-id="element-3387">
              <div className="w-12 h-12 bg-[#EA580C] rounded-xl flex items-center justify-center mb-6 shadow-lg shadow-[#EA580C]/20" data-id="element-3388">
                <Calendar className="w-6 h-6 text-white" data-id="element-3389" />
              </div>
              <h1 className="font-title font-bold text-2xl text-white mb-4" data-id="element-3390">
                Giga Live Demo
              </h1>
              <div className="space-y-4 text-neutral-400" data-id="element-3391">
                <div className="flex items-center gap-3" data-id="element-3392">
                  <Clock className="w-5 h-5" data-id="element-3393" />
                  <span data-id="element-3394">30 min</span>
                </div>
                <div className="flex items-center gap-3" data-id="element-3395">
                  <Video className="w-5 h-5" data-id="element-3396" />
                  <span data-id="element-3397">
                    Web conferencing details provided upon confirmation.
                  </span>
                </div>
              </div>
              <p className="mt-8 text-sm text-neutral-500 leading-relaxed" data-id="element-3398">
                We'll show you how Giga can automate your specific workflows and
                answer any questions you have about security, integration, and
                pricing.
              </p>
            </div>

            {/* Right calendar panel */}
            <div className="p-8 md:p-10 md:w-2/3 flex flex-col md:flex-row gap-8" data-id="element-3399">
              <div className="flex-1" data-id="element-3400">
                <h2 className="font-bold text-lg text-white mb-6" data-id="element-3401">
                  Select a Date & Time
                </h2>

                <div className="flex items-center justify-between mb-6" data-id="element-3402">
                  <span className="text-white font-medium" data-id="element-3403">June 2026</span>
                  <div className="flex gap-2" data-id="element-3404">
                    <button className="p-2 rounded-lg hover:bg-white/5 text-neutral-400 transition-colors" data-id="element-3405">
                      <ChevronLeft className="w-5 h-5" data-id="element-3406" />
                    </button>
                    <button className="p-2 rounded-lg hover:bg-white/5 text-neutral-400 transition-colors" data-id="element-3407">
                      <ChevronRight className="w-5 h-5" data-id="element-3408" />
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-7 gap-2 mb-2" data-id="element-3409">
                  {['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'].map(day => <div key={day} className="text-center text-xs font-bold text-neutral-500 py-2" data-id="element-3410">
                        {day}
                      </div>)}
                </div>

                <div className="grid grid-cols-7 gap-2" data-id="element-3411">
                  {/* Empty slots for offset */}
                  <div className="aspect-square" data-id="element-3412"></div>

                  {Array.from({
                length: 30
              }).map((_, i) => {
                const date = i + 1;
                const isPast = date < 15;
                const isSelected = selectedDate === date;
                return <button key={date} disabled={isPast} onClick={() => {
                  setSelectedDate(date);
                  setSelectedTime(null);
                }} className={`
                          aspect-square rounded-full flex items-center justify-center text-sm font-medium transition-all
                          ${isPast ? 'text-neutral-600 cursor-not-allowed' : isSelected ? 'bg-[#EA580C] text-white shadow-lg shadow-[#EA580C]/20' : 'text-neutral-300 hover:bg-white/10 hover:text-white'}
                        `} data-id="element-3413">
                        {date}
                      </button>;
              })}
                </div>

                <div className="mt-8 flex items-center gap-2 text-sm text-neutral-500" data-id="element-3414">
                  <Globe className="w-4 h-4" data-id="element-3415" />
                  <span data-id="element-3416">Pacific Time - US & Canada</span>
                </div>
              </div>

              {/* Time slots (shows when date is selected) */}
              {selectedDate && <motion.div initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} className="md:w-48 flex flex-col gap-3" data-id="element-3417">
                  <h3 className="font-medium text-white mb-3" data-id="element-3418">
                    {new Date(2026, 5, selectedDate).toLocaleDateString('en-US', {
                weekday: 'long',
                month: 'long',
                day: 'numeric'
              })}
                  </h3>
                  <div className="flex flex-col gap-2 h-[300px] overflow-y-auto pr-2 custom-scrollbar" data-id="element-3419">
                    {timeSlots.map(time => <div key={time} className="flex gap-2" data-id="element-3420">
                        <button onClick={() => setSelectedTime(time)} className={`
                            flex-1 py-3 rounded-xl text-sm font-medium transition-all border
                            ${selectedTime === time ? 'bg-neutral-700 border-neutral-600 text-white' : 'bg-transparent border-white/10 text-[#EA580C] hover:border-[#EA580C]'}
                          `} data-id="element-3421">
                          {time}
                        </button>
                        {selectedTime === time && <button onClick={handleBook} className="bg-[#EA580C] text-white px-4 rounded-xl text-sm font-bold hover:bg-[#C2410C] transition-colors shadow-lg shadow-[#EA580C]/20 whitespace-nowrap" data-id="element-3422">
                            Next
                          </button>}
                      </div>)}
                  </div>
                </motion.div>}
            </div>
          </motion.div> : <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} className="bg-[#2B211A] border border-white/10 rounded-3xl shadow-2xl p-12 text-center max-w-2xl mx-auto" data-id="element-3423">
            <div className="w-20 h-20 bg-[#EA580C]/10 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-3424">
              <div className="w-14 h-14 bg-[#EA580C] rounded-full flex items-center justify-center shadow-lg shadow-[#EA580C]/30" data-id="element-3425">
                <Check className="w-7 h-7 text-white" data-id="element-3426" />
              </div>
            </div>
            <h2 className="font-title font-bold text-3xl text-white mb-4" data-id="element-3427">
              You are scheduled
            </h2>
            <p className="text-neutral-400 text-lg max-w-md mx-auto mb-8" data-id="element-3428">
              A calendar invitation has been sent to your email address. We look
              forward to showing you Giga in action.
            </p>
            <button onClick={() => window.location.href = '/'} className="text-neutral-400 hover:text-white font-medium transition-colors" data-id="element-3429">
              Return to home
            </button>
          </motion.div>}
      </div>
    </div>;
}